import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Textarea,
  Select,
  Option,
  IconButton,
  Modal,
  ModalDialog,
  ModalClose,
  Tooltip,
} from "@mui/joy";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UploadIcon from "@mui/icons-material/Upload";

import { Assignment } from "../../../api/assignments/Assignments.domain";
import { iconCtaSx } from "../../../theme";
import {
  RefuseMissionReasonCode,
  AssignmentAcceptanceStatus,
  MissionStatus,
} from "../../../api/assignments/Assignments.codes";
import { UpdateAssignmentRequestInput } from "../../../api/assignments/Assignments.responses";

import { useAssignments } from "../../../contexts/controllers/useAssignments";
import { useAlert } from "../../../hooks/useAlert";
import { useConfirmation } from "../../../contexts/ConfirmationContext";
import { useRules } from "../../../contexts/controllers/useRules";
import { haveActivitiesChanged } from "../../../utils/assignmentComparison";
import { validateActivitiesList } from "../../../utils/activityValidation";

import AssignmentEditorFields from "./AssignmentsEditorFields";
import AssignmentReadOnlyFields from "./AssignmentsReadOnlyFields";
import AssignmentHeaderContent from "./AssignmentsHeaderContent";

interface AssignmentCardProps {
  assignment: Assignment;
  showAcceptanceActions?: boolean;
  editMode?: boolean;
  onActionComplete?: () => void;
}

const AssignmentCard: React.FC<AssignmentCardProps> = ({
  assignment,
  showAcceptanceActions = false,
  editMode = false,
  onActionComplete,
}) => {
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [originalAssignment, setOriginalAssignment] = React.useState<Assignment | null>(null);
  const [activitiesChanged, setActivitiesChanged] = React.useState(false);

  const [isRejecting, setIsRejecting] = React.useState(false);
  const [rejectReasonCode, setRejectReasonCode] = React.useState<RefuseMissionReasonCode>(
    RefuseMissionReasonCode.PROVIDER_NOT_AVAILABLE,
  );
  const [freeField, setFreeField] = React.useState("");

  const { updateAcceptance, updateAssignment, getAssignment, updateMissionStatus } =
    useAssignments();
  const { showAlert } = useAlert();
  const { confirm } = useConfirmation();
  const { rules } = useRules();

  const [localAssignment, setLocalAssignment] = React.useState<Assignment>(assignment);

  React.useEffect(() => {
    setLocalAssignment(assignment);
  }, [assignment]);

  const checkActivitiesChanges = React.useCallback(() => {
    return haveActivitiesChanged(originalAssignment, localAssignment);
  }, [localAssignment, originalAssignment]);

  const prevActivitiesChangedRef = React.useRef<boolean>(false);
  React.useEffect(() => {
    if (!editMode || !detailsOpen) return;
    const hasChanged = checkActivitiesChanges();
    if (prevActivitiesChangedRef.current !== hasChanged) {
      prevActivitiesChangedRef.current = hasChanged;
      setActivitiesChanged(hasChanged);
    }
  }, [localAssignment, checkActivitiesChanges, editMode, detailsOpen]);

  const handleOpenDetails = async () => {
    if (editMode) {
      setLoading(true);
      try {
        // Fetch the latest version from the API for comparison
        const result = await getAssignment(assignment.externalAssignmentId);
        if (result?.response) {
          setOriginalAssignment(result.response);
          setLocalAssignment({ ...result.response });
        } else {
          // Fallback if the API does not respond
          setOriginalAssignment({ ...assignment });
          setLocalAssignment({ ...assignment });
        }
      } catch (err) {
        showAlert("Échec de récupération des données actuelles", "danger");
        setOriginalAssignment({ ...assignment });
        setLocalAssignment({ ...assignment });
      } finally {
        setActivitiesChanged(false); // Reset state on load
        setLoading(false);
      }
    }
    setDetailsOpen(true);
  };

  const handleCloseDetails = async () => {
    // Refresh data if needed
    if (editMode) {
      onActionComplete?.();
    }
    // Close the modal
    setDetailsOpen(false);
  };

  const validateActivities = (): boolean => {
    if (!rules) return true;
    const result = validateActivitiesList(localAssignment.activities ?? [], rules);
    if (!result.valid && result.message) {
      showAlert(result.message, "warning");
    }
    return result.valid;
  };

  const handleSave = () => {
    // Validate activities before prompting to save
    if (!validateActivities()) {
      return;
    }

    confirm({
      title: "Publier vers SPB ?",
      message: "Êtes-vous sûr de vouloir publier cette activité vers SPB ?",
      onConfirm: async () => {
        setLoading(true);
        try {
          const updateData: UpdateAssignmentRequestInput = {
            request: { ...localAssignment },
          };

          const result = await updateAssignment(localAssignment.externalAssignmentId, updateData);

          if (result?.response) {
            setLocalAssignment(result.response);
            showAlert("Mission sauvegardée avec succès !", "success");

            // Fetch the refreshed version from the API for comparison
            const refreshedAssignment = await getAssignment(localAssignment.externalAssignmentId);
            if (refreshedAssignment?.response) {
              setOriginalAssignment(refreshedAssignment.response);
              // Now that original/local are in sync, the diff should be cleared
              setActivitiesChanged(false);
            }
          }
        } catch (err) {
          showAlert(
            "Échec de la sauvegarde de la mission. Consultez la console pour plus de détails.",
            "danger",
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleAccept = () => {
    confirm({
      title: "Accepter la mission",
      message: "Êtes-vous sûr de vouloir accepter cette mission ?",
      onConfirm: async () => {
        setLoading(true);
        // 1. Accept the mission
        const acceptResult = await updateAcceptance(assignment.externalAssignmentId, {
          servicePointCode: assignment.servicePoint?.servicePointCode ?? "",
          acceptanceStatus: AssignmentAcceptanceStatus.ACCEPTED,
        });

        if (acceptResult) {
          // 2. Update mission status to IN_PROGRESS
          const missionStatusResult = await updateMissionStatus(assignment.externalAssignmentId, {
            request: {
              missionStatus: MissionStatus.IN_PROGRESS,
            },
          });

          if (missionStatusResult) {
            onActionComplete?.();
            setDetailsOpen(false);
          }
        }
        setLoading(false);
      },
    });
  };

  const handleReject = () => {
    if (!rejectReasonCode) {
      showAlert("Veuillez sélectionner une raison", "danger");
      return;
    }
    confirm({
      title: "Refuser la mission",
      message: "Êtes-vous sûr de vouloir refuser cette mission ?",
      onConfirm: async () => {
        setLoading(true);
        const result = await updateAcceptance(assignment.externalAssignmentId, {
          servicePointCode: assignment.servicePoint?.servicePointCode ?? "",
          acceptanceStatus: AssignmentAcceptanceStatus.REJECTED,
          rejectReason: {
            code: rejectReasonCode,
            freeField: freeField.trim(),
          },
        });

        if (result) {
          setIsRejecting(false);
          setRejectReasonCode(RefuseMissionReasonCode.PROVIDER_NOT_AVAILABLE);
          setFreeField("");
          onActionComplete?.();
          setDetailsOpen(false);
        }
        setLoading(false);
      },
    });
  };

  const handleAssignmentUpdateNoChange = React.useCallback((updated: Assignment) => {
    setLocalAssignment(updated);
  }, []);

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: "clamp(360px, 90vw, 960px)",
          margin: "0 auto",
          borderRadius: "md",
          boxShadow: "sm",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: "md",
            transform: "translateY(-1px)",
          },
        }}
      >
        <CardContent sx={{ py: 1, px: 1, "&:last-child": { pb: 1.5 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <AssignmentHeaderContent assignment={assignment} editMode={editMode} />

            <IconButton size="sm" variant="plain" onClick={handleOpenDetails} sx={iconCtaSx}>
              <OpenInNewIcon />
            </IconButton>
          </Box>
        </CardContent>
      </Card>

      <Modal open={detailsOpen} onClose={handleCloseDetails}>
        <ModalDialog
          layout="center"
          sx={{
            width: { xs: "95vw", sm: "90vw", md: "80vw", lg: "70vw" },
            maxWidth: "1200px",
            maxHeight: "90vh",
            overflowY: "auto",
            overflowX: "hidden",
            padding: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{
              position: "sticky",
              top: 0,
              bgcolor: "background.surface",
              zIndex: 1050,
              px: 3,
              pt: 2,
              pb: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <AssignmentHeaderContent assignment={assignment} editMode={editMode} />

            <ModalClose sx={{ position: "relative", top: -4, right: -4 }} />
          </Box>

          <Box
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            {editMode ? (
              <>
                <AssignmentEditorFields
                  assignment={localAssignment}
                  onChange={handleAssignmentUpdateNoChange}
                  activitiesChanged={activitiesChanged}
                />
                {activitiesChanged &&
                  localAssignment.activities &&
                  localAssignment.activities.length > 0 && (
                    <Box
                      sx={{
                        position: "sticky",
                        bottom: 16,
                        right: 16,
                        marginLeft: "auto",
                        width: "fit-content",
                        zIndex: 1100,
                      }}
                    >
                      <Tooltip title="Envoi SPB" placement="top">
                        <IconButton
                          size="lg"
                          variant="solid"
                          color="success"
                          onClick={handleSave}
                          disabled={loading}
                          sx={{ borderRadius: "50%", boxShadow: "md" }}
                        >
                          <UploadIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
              </>
            ) : (
              <AssignmentReadOnlyFields assignment={assignment} />
            )}

            {showAcceptanceActions && !editMode && (
              <Box
                mt={3}
                display="flex"
                gap={2}
                sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}
              >
                <Button
                  size="sm"
                  startDecorator={<CheckIcon />}
                  onClick={handleAccept}
                  disabled={loading}
                  sx={{
                    backgroundColor: "#4CAF50",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#45A049" },
                  }}
                >
                  Accepter
                </Button>
                <Button
                  size="sm"
                  startDecorator={<CloseIcon />}
                  onClick={() => setIsRejecting(true)}
                  disabled={loading}
                  sx={{
                    backgroundColor: "#F44336",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#E53935" },
                  }}
                >
                  Refuser
                </Button>
              </Box>
            )}

            {isRejecting && !editMode && (
              <Box mt={2} p={2} border="1px solid" borderColor="divider" borderRadius="md">
                <Typography fontWeight="bold">Motif de refus :</Typography>
                <Select
                  placeholder="Sélectionner un motif"
                  value={rejectReasonCode}
                  onChange={(e, newVal) => {
                    setRejectReasonCode(
                      (newVal as RefuseMissionReasonCode) ??
                        RefuseMissionReasonCode.PROVIDER_NOT_AVAILABLE,
                    );
                  }}
                  sx={{ mt: 1, width: "100%" }}
                >
                  <Option value={RefuseMissionReasonCode.PROVIDER_NOT_AVAILABLE}>
                    Prestataire non disponible
                  </Option>
                  <Option value={RefuseMissionReasonCode.NON_COMPLIANT}>Non conforme</Option>
                </Select>
                <Textarea
                  placeholder="Détails (optionnel)"
                  value={freeField}
                  onChange={(e) => setFreeField(e.target.value)}
                  minRows={3}
                  sx={{ mt: 2 }}
                />
                <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
                  <Button
                    size="sm"
                    variant="plain"
                    onClick={() => {
                      setIsRejecting(false);
                      setRejectReasonCode(RefuseMissionReasonCode.PROVIDER_NOT_AVAILABLE);
                      setFreeField("");
                    }}
                  >
                    Annuler
                  </Button>
                  <Button
                    size="sm"
                    variant="solid"
                    disabled={loading}
                    onClick={handleReject}
                    sx={{ backgroundColor: "#F44336", color: "#fff" }}
                  >
                    Confirmer le refus
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default AssignmentCard;
